// inferStepKind — keyword-driven classification of a recipe step's
// "type of cooking action." Used to pick the right SF Symbol in the
// Dynamic Island compact-leading slot ("am I in a sear right now or
// a bake?") so the user can read the step at a glance without ever
// expanding the activity.
//
// Inputs: step title (primary signal) + optional instruction (backup
// when the title is too vague — e.g., "Step 3" with the actual
// cooking verb buried in the prose).
//
// Returns one of:
//   "sear" | "boil" | "bake" | "grill" | "steam" | "mix" | "prep"
//   | "rest" | "finish" | "default"
//
// The regex order matters — more specific patterns win. "Sear the
// chicken until golden brown" matches sear, not brown. "Bake at
// 425" matches bake before any incidental mix verbs in the prose.
//
// Conservative on misclassification: an unrecognized step falls
// through to "default" (which maps to a neutral SF Symbol). Better
// to show a generic timer than to lie about the cooking action.

export function inferStepKind(title, instruction) {
  const text = `${title || ""} ${instruction || ""}`.toLowerCase();
  // Order matters: specific cooking verbs first, generic prep last.
  // SEAR — high-heat surface cooking. "Sauté" + accented variant.
  if (/\b(sear|brown|fry|saut[ée]|caramelize|crisp|render)\b/.test(text)) return "sear";
  // GRILL — char + open flame. Distinct from sear so we can show a
  // pure flame icon. Falls before "bake" since grill recipes often
  // mention oven temps as a secondary instruction.
  if (/\b(grill|char|broil)\b/.test(text)) return "grill";
  // BOIL — water-based. Includes reductions and poaching since both
  // are pot-of-liquid work.
  if (/\b(boil|simmer|poach|reduce|deglaze|blanch|parcook)\b/.test(text)) return "boil";
  // STEAM — separate from boil since the icon convention is
  // different (steaming basket vs rolling water).
  if (/\b(steam)\b/.test(text)) return "steam";
  // BAKE — dry oven heat. "Roast" lands here vs grill because the
  // oven is enclosed (no direct flame).
  if (/\b(bake|roast|oven|braise)\b/.test(text)) return "bake";
  // MIX — combining ingredients. Whisks, folds, beats.
  if (/\b(mix|stir|whisk|fold|combine|blend|beat|emulsify)\b/.test(text)) return "mix";
  // PREP — knife work + raw-ingredient prep. Last cooking verb
  // before falling through; "cut" is broad enough to catch most
  // prep-step phrasings.
  if (/\b(chop|dice|mince|slice|julienne|cut|peel|grate|prep|trim)\b/.test(text)) return "prep";
  // REST — passive wait. Doughs proofing, meats resting post-cook,
  // chill in fridge. Distinct icon so the user knows "you're
  // waiting, not actively cooking."
  if (/\b(rest|cool|chill|set|proof|rise|marinate)\b/.test(text)) return "rest";
  // FINISH — plating, garnishing, final touches. Last logical step
  // type before generic fallback.
  if (/\b(plate|serve|garnish|finish|drizzle|sprinkle|top with)\b/.test(text)) return "finish";
  return "default";
}
